import { cloneDeep, compact, max, sumBy } from 'lodash';
import { BallType, PassResult } from '../enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_VELO_DELTA_MPH,
} from '../enums/pitch-performance';
import { DictOf } from '../interfaces/common/i-dictionary';
import {
  EMPTY_METRIC_DETAILS,
  EMPTY_SHOT_ACTUALS,
  IFireWithMetrics,
  IGroupedFires,
  IMetricDetails,
  IMetricTally,
  IPitchPerformanceFire,
  IShotActuals,
} from '../interfaces/data/i-pitch-performance';

import { IRealMachinePerformance } from '../interfaces/modelling/i-real-machine-metric';
import {
  IBallState,
  IPitch,
  IPlateLoc,
  ITrajectory,
  ITrajektRefBreak,
} from '../interfaces/pitches';
import { IMachineShot } from '../interfaces/training/i-machine-shot';
import { ITrainedMachineState } from '../interfaces/training/i-trained-machine-state';
import { ArrayHelper } from './array.helper';
import { BallHelper } from './ball.helper';
import { EllipseHelper } from './ellipse.helper';
import { FT_TO_INCHES, mean, meanObject } from './math.utilities';
import { MiscHelper } from './misc.helper';
import { TrajHelper } from './trajectory.helper';

export const SKIP_OVERALL_CHECKS = true;

export class MachinePerformanceHelper {
  /** true => model passes, false => fails */
  static checkOverall = (value: IRealMachinePerformance): boolean => {
    if (SKIP_OVERALL_CHECKS) {
      return true;
    }

    return [
      this.checkSpeedY(value) === PassResult.Pass,
      this.checkSpinOverall(value),
      this.checkPlateOverall(value),
    ].every((v) => v === true);
  };

  static checkSpeedY = (value: IRealMachinePerformance): PassResult => {
    const MEAN_AE = 1.4;
    const MAX_AE = 2.8;

    if (value.mean_stdev.vy > MEAN_AE && value.max_stdev.vy > MAX_AE) {
      return PassResult.Both;
    }

    if (value.mean_stdev.vy > MEAN_AE) {
      return PassResult.Mean;
    }

    if (value.max_stdev.vy > MAX_AE) {
      return PassResult.Max;
    }

    return PassResult.Pass;
  };

  static checkPlateOverall = (value: IRealMachinePerformance): boolean => {
    return [
      this.checkPlateDimension(value.mean_stdev.plx, value.max_stdev.plx),
      this.checkPlateDimension(value.mean_stdev.plz, value.max_stdev.plz),
    ].every((v) => v === PassResult.Pass);
  };

  static checkPlateDimension = (mean: number, max: number): PassResult => {
    // inches
    const MEAN_AE = 5.5;
    const MAX_AE = 10;

    if (mean > MEAN_AE && max > MAX_AE) {
      return PassResult.Both;
    }
    if (mean > MEAN_AE) {
      return PassResult.Mean;
    }

    if (max > MAX_AE) {
      return PassResult.Max;
    }

    return PassResult.Pass;
  };

  static checkSpinOverall = (value: IRealMachinePerformance): boolean => {
    return [
      this.checkSpinDimension(value.mean_stdev.wx, value.max_stdev.wx),
      this.checkSpinDimension(value.mean_stdev.wy, value.max_stdev.wy),
      this.checkSpinDimension(value.mean_stdev.wz, value.max_stdev.wz),
    ].every((v) => v === PassResult.Pass);
  };

  // clones input before adding averages and deltas to the grouped fires
  static cloneWithAvgAndDelta = (input: IGroupedFires): IGroupedFires => {
    const output = cloneDeep(input);

    output.rotatedShots = EllipseHelper.getRotatedShots({
      ms: output.target.ms,
      shots: output.fires.map((f) => f.shot),
      releaseFt: output.target.ms.py,
    });

    const validBss = output.rotatedShots
      .filter((s) => s.bs)
      .map((s) => s.bs as IBallState);

    const validTrajs = output.rotatedShots
      .filter((s) => s.traj)
      .map((s) => s.traj as ITrajectory);

    const speedMPHs: number[] = validTrajs.map((traj) =>
      TrajHelper.getSpeedFromTraj(traj)
    );

    const netSpins: number[] = validBss.map((bs) => BallHelper.getNetSpin(bs));

    const breaks: ITrajektRefBreak[] = output.rotatedShots.map((s) =>
      TrajHelper.getBreaksFromShot(s)
    );

    const plates: IPlateLoc[] = validTrajs.map((traj) =>
      TrajHelper.getPlateLoc(traj)
    );

    if (speedMPHs.length > 0) {
      output.average.speedMPH = mean(speedMPHs);
    }

    if (netSpins.length > 0) {
      output.average.netSpin = mean(netSpins);
    }

    if (breaks.length > 0) {
      output.average.break = meanObject(breaks) as ITrajektRefBreak;
    }

    if (plates.length > 0) {
      output.average.plate = meanObject(plates) as IPlateLoc;
    }

    output.delta.speedMPH = output.average.speedMPH - output.target.speedMPH;
    output.delta.netSpin = output.average.netSpin - output.target.netSpin;
    output.delta.break = {
      xInches: output.average.break.xInches - output.target.break.xInches,
      zInches: output.average.break.zInches - output.target.break.zInches,
    };
    output.delta.plate = {
      plate_x: output.average.plate.plate_x - output.target.plate.plate_x,
      plate_z: output.average.plate.plate_z - output.target.plate.plate_z,
    };

    return output;
  };

  static checkSpinDimension = (mean: number, max: number): PassResult => {
    const MEAN_AE = 650;
    const MAX_AE = 1_000_000;

    if (mean > MEAN_AE && max > MAX_AE) {
      return PassResult.Both;
    }
    if (mean > MEAN_AE) {
      return PassResult.Mean;
    }

    if (max > MAX_AE) {
      return PassResult.Max;
    }

    return PassResult.Pass;
  };

  static getFireGroup = (values: IPitchPerformanceFire[]): IGroupedFires[] => {
    // key is matching_hash
    const groupDict = ArrayHelper.groupByFn(
      values,
      (f) => f.shot.target_ms.matching_hash
    );

    const output: IGroupedFires[] = [];

    for (const matching_hash of Object.keys(groupDict)) {
      const fires = groupDict[matching_hash];

      if (!fires) {
        continue;
      }

      if (fires.length === 0) {
        continue;
      }

      const firstFire = fires.find(
        (f) => !!f.shot.target_traj && !!f.shot.target_bs && !!f.shot.plate
      );

      if (!firstFire) {
        continue;
      }

      const { shot } = firstFire;

      if (!shot.target_traj) {
        continue;
      }

      if (!shot.target_bs) {
        continue;
      }

      if (!shot.plate) {
        continue;
      }

      // create an entry with the target on first encounter
      const rawGroup: IGroupedFires = {
        // uniquely identify pitch x ms combinations
        _id: `${shot.pitch_id}-${matching_hash}`,

        rgb: MiscHelper.getRGB(shot.pitch_id ?? 'no pitch ID'),

        pitch: firstFire.pitch,

        // does the matching_hash match the pitch's (current) TMS matching_hash
        isCurrentMs:
          matching_hash === firstFire.pitch._mx_tms?.ms.matching_hash,

        lastFired: max(fires.map((f) => f.shot._created)),

        target: {
          ms: shot.target_ms,
          traj: shot.target_traj,

          speedMPH: BallHelper.getSpeedFromBs(shot.target_bs),
          netSpin: BallHelper.getNetSpin(shot.target_bs),
          plate: shot.plate,
          break:
            // prefer pitch's breaks, if any, before defaulting to traj's breaks
            shot.target_ms.pitchBreaks ??
            TrajHelper.getBreaks(shot.target_traj),
        },

        // will be computed once all fires are assembled
        average: {
          speedMPH: 0,
          netSpin: 0,
          plate: {
            plate_x: 0,
            plate_z: 0,
          },
          break: {
            xInches: 0,
            zInches: 0,
          },
        },

        // will be computed once all fires are assembled
        delta: {
          speedMPH: 0,
          netSpin: 0,
          plate: {
            plate_x: 0,
            plate_z: 0,
          },
          break: {
            xInches: 0,
            zInches: 0,
          },
        },

        fires: fires,

        rotatedShots: [],
      };

      output.push(this.cloneWithAvgAndDelta(rawGroup));
    }

    return output;
  };

  static getCountMetric(
    target: {
      speedMPH: number;
      plate: IPlateLoc;
      break: ITrajektRefBreak;
    },
    shot: IMachineShot
  ): IMetricDetails {
    const empty = cloneDeep(EMPTY_METRIC_DETAILS);

    if (!shot.target_traj) {
      return empty;
    }

    if (!shot.traj) {
      return empty;
    }

    const actual = this.getShotActuals(shot);

    const veloOK =
      Math.abs(target.speedMPH - actual.speedMPH) <= MAX_VELO_DELTA_MPH;

    const MAX_BREAKS_DELTA_INCHES =
      shot.ball_type === BallType.Trajekt_36
        ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
        : MAX_BREAKS_DELTA_IN;

    const breakDistanceIn = Math.sqrt(
      Math.pow(target.break.xInches - actual.break.xInches, 2) +
        Math.pow(target.break.zInches - actual.break.zInches, 2)
    );
    const breakOK = breakDistanceIn <= MAX_BREAKS_DELTA_INCHES;

    const plateDistanceFt = Math.sqrt(
      Math.pow(target.plate.plate_x - actual.plate.plate_x, 2) +
        Math.pow(target.plate.plate_z - actual.plate.plate_z, 2)
    );
    const plateOK = plateDistanceFt * FT_TO_INCHES <= MAX_PLATE_DELTA_IN;

    const o: IMetricDetails = {
      total: 1,
      overall: veloOK && breakOK && plateOK ? 1 : 0,
      velocity: veloOK ? 1 : 0,
      break: breakOK ? 1 : 0,
      plate: plateOK ? 1 : 0,
    };

    return o;
  }

  static getTallyFromShotIds = (
    groups: IGroupedFires[],
    shotIds: string[]
  ): IMetricTally => {
    const shots = groups.flatMap((group) =>
      group.rotatedShots.filter((shot) => shotIds.includes(shot._id))
    );

    // calculate the metric for each shot from each group
    const metrics = compact(
      shots.flatMap((shot) => {
        if (
          !shot ||
          !shot.target_traj ||
          !shot.traj ||
          !shot.target_bs ||
          !shot.plate
        ) {
          return;
        }

        const targetSpeedMPH = BallHelper.getSpeedFromBs(shot.target_bs);
        const targetBreak = TrajHelper.getBreaks(shot.target_traj);

        const target = {
          speedMPH: targetSpeedMPH,
          plate: shot.plate,
          break: targetBreak,
        };

        return this.getCountMetric(target, shot);
      })
    );

    // aggregate results into a total
    const counts: IMetricDetails = {
      total: sumBy(metrics, (m) => m.total),
      overall: sumBy(metrics, (m) => m.overall),
      velocity: sumBy(metrics, (m) => m.velocity),
      break: sumBy(metrics, (m) => m.break),
      plate: sumBy(metrics, (m) => m.plate),
    };

    const output: IMetricTally = {
      counts: counts,
      percents: this.getPercentMetric(counts),
    };

    return output;
  };

  static getTallyFromGroups = (groups: IGroupedFires[]): IMetricTally => {
    // calculate the metric for each shot from each group
    const metrics = groups.flatMap((group) =>
      group.rotatedShots.flatMap((shot) =>
        this.getCountMetric(group.target, shot)
      )
    );

    // aggregate results into a total
    const counts: IMetricDetails = {
      total: sumBy(metrics, (m) => m.total),
      overall: sumBy(metrics, (m) => m.overall),
      velocity: sumBy(metrics, (m) => m.velocity),
      break: sumBy(metrics, (m) => m.break),
      plate: sumBy(metrics, (m) => m.plate),
    };

    const output: IMetricTally = {
      counts: counts,
      percents: this.getPercentMetric(counts),
    };

    return output;
  };

  // determines if the pitch's average speed, break, and location are within spec
  static getCountAverageMetric(
    target: {
      speedMPH: number;
      plate: IPlateLoc;
      break: ITrajektRefBreak;
    },
    average: {
      speedMPH: number;
      plate: IPlateLoc;
      break: ITrajektRefBreak;
    },
    ball_type?: BallType
  ): IMetricDetails {
    const emptyResult = cloneDeep(EMPTY_METRIC_DETAILS);

    const {
      break: averageBreak,
      plate: averagePlate,
      speedMPH: averageVelo,
    } = average;

    if (!averageBreak || !averageVelo || !averagePlate) {
      return emptyResult;
    }

    const veloOK =
      Math.abs(target.speedMPH - averageVelo) <= MAX_VELO_DELTA_MPH;

    const MAX_BREAKS_DELTA_INCHES =
      ball_type === BallType.Trajekt_36
        ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
        : MAX_BREAKS_DELTA_IN;

    const breakDistanceIn = Math.sqrt(
      Math.pow(target.break.xInches - averageBreak.xInches, 2) +
        Math.pow(target.break.zInches - averageBreak.zInches, 2)
    );

    const breakOK = breakDistanceIn <= MAX_BREAKS_DELTA_INCHES;

    const plateDistanceFt = Math.sqrt(
      Math.pow(target.plate.plate_x - averagePlate.plate_x, 2) +
        Math.pow(target.plate.plate_z - averagePlate.plate_z, 2)
    );

    const plateOK = plateDistanceFt * FT_TO_INCHES <= MAX_PLATE_DELTA_IN;

    const o: IMetricDetails = {
      total: 1,
      overall: veloOK && breakOK && plateOK ? 1 : 0,
      velocity: veloOK ? 1 : 0,
      break: breakOK ? 1 : 0,
      plate: plateOK ? 1 : 0,
    };

    return o;
  }

  static getAverageTallyFromGroups = (
    groups: IGroupedFires[]
  ): IMetricTally => {
    const ball_type = groups[0]?.rotatedShots[0]?.ball_type;

    // calculate the metric for the average of each group
    const metrics = groups.flatMap((group) =>
      this.getCountAverageMetric(group.target, group.average, ball_type)
    );

    // aggregate results into a total
    const counts: IMetricDetails = {
      total: sumBy(metrics, (m) => m.total),
      overall: sumBy(metrics, (m) => m.overall),
      velocity: sumBy(metrics, (m) => m.velocity),
      break: sumBy(metrics, (m) => m.break),
      plate: sumBy(metrics, (m) => m.plate),
    };

    const output: IMetricTally = {
      counts: counts,
      percents: this.getPercentMetric(counts),
    };

    return output;
  };

  static getPercentMetric(count: IMetricDetails): IMetricDetails {
    if (count.total === 0) {
      return { ...EMPTY_METRIC_DETAILS };
    }

    const output: IMetricDetails = {
      total: 100,
      overall: 100 * (count.overall / count.total),
      velocity: 100 * (count.velocity / count.total),
      break: 100 * (count.break / count.total),
      plate: 100 * (count.plate / count.total),
    };

    return output;
  }

  // e.g. converts a count good into count bad and vice-versa
  static invertCountMetric(count: IMetricDetails): IMetricDetails {
    const output: IMetricDetails = {
      total: count.total,
      overall: count.total - count.overall,
      velocity: count.total - count.velocity,
      break: count.total - count.break,
      plate: count.total - count.plate,
    };

    return output;
  }

  // e.g. converts a % good into % bad and vice-versa
  static invertPercentMetric(percent: IMetricDetails): IMetricDetails {
    const output: IMetricDetails = {
      total: 100,
      overall: 100 - percent.overall,
      velocity: 100 - percent.velocity,
      break: 100 - percent.break,
      plate: 100 - percent.plate,
    };

    return output;
  }

  static getShotActuals(shot: IMachineShot): IShotActuals {
    const empty = cloneDeep(EMPTY_SHOT_ACTUALS);

    if (!shot.target_traj) {
      return empty;
    }

    if (!shot.traj) {
      return empty;
    }

    return {
      speedMPH: TrajHelper.getSpeedFromTraj(shot.traj),
      break: TrajHelper.getBreaksFromShot(shot),
      plate: TrajHelper.getPlateLoc(shot.traj),
    };
  }

  static getFireGroupMetrics(group: IGroupedFires): IFireWithMetrics[] {
    const shots = group.fires.map((s) => s.shot);

    const metrics = shots.map((shot) => {
      if (
        !shot ||
        !shot.target_traj ||
        !shot.traj ||
        !shot.target_bs ||
        !shot.plate
      ) {
        return undefined;
      }

      const actuals = this.getShotActuals(shot);

      const targetSpeedMPH = BallHelper.getSpeedFromBs(shot.target_bs);

      return {
        _id: shot._id,
        speedMPH: actuals.speedMPH,
        plate: actuals.plate,
        break: actuals.break,
        target: {
          ms: shot.target_ms,
          traj: shot.target_traj,

          speedMPH: targetSpeedMPH,
          plate: shot.plate,

          // aka: from pitch definition
          break: group.target.break,
        },
        delta: {
          speedMPH: actuals.speedMPH - targetSpeedMPH,
          break: {
            xInches: actuals.break.xInches - group.target.break.xInches,
            zInches: actuals.break.zInches - group.target.break.zInches,
          },
          plate: {
            plate_x: actuals.plate.plate_x - shot.plate.plate_x,
            plate_z: actuals.plate.plate_z - shot.plate.plate_z,
          },
        },
        shot,
      };
    });

    return compact(metrics);
  }

  static getFire = (
    shot: IMachineShot,
    pitchDict: DictOf<IPitch>,
    // key is bs_hash, only necessary for determining current training status
    tmsDict?: DictOf<ITrainedMachineState>
  ): IPitchPerformanceFire | undefined => {
    if (!shot.pitch_id) {
      return;
    }

    if (!shot.ball_type) {
      return;
    }

    const pitch = pitchDict[shot.pitch_id];

    if (!pitch) {
      return;
    }

    if (!pitch.type) {
      return;
    }

    const tms = tmsDict?.[pitch.bs_hash];

    const { target_ms } = shot;

    // copy breaks from pitch into shot
    target_ms.pitchBreaks = pitch.breaks;

    // only set PT flag if the matching hash belongs to the pitch's current ms
    if (target_ms.matching_hash === tms?.ms?.matching_hash) {
      target_ms.precision_trained = tms?.ms?.precision_trained;
    }

    const o: IPitchPerformanceFire = {
      pitch: {
        _id: shot.pitch_id, // because pitch._id might be an ObjectId on the server but we don't have stringifyID in lib_ts
        type: pitch.type,
        _mx_tms: tms,
        bs_hash: pitch.bs_hash,
        name: pitch.name ?? 'Unnamed Pitch',
        priority: pitch.priority,
      },
      shot: shot,
    };

    return o;
  };
}
